Px.Editor.CutPrints = class CutPrints extends Px.Editor.BaseComponent {

  template() {
    const store = this.data.store;
    const r = this.renderChild;
    return Px.template`
      <div class="px-cut-prints ${this.state.drop_active ? 'px-drop-active' : ''}"
           data-active="${store.main_view === 'cut-prints'}"
           data-ondrop="onItemsDropped"
           data-ondragenter="onDragEnter"
           data-ondragover="onDragOver"
           data-ondragleave="onDragLeave">
        ${Px.if(this.printSetsWithDefinitions.length > 0, () => {
          return Px.template`
            ${Px.if(!store.ui.share_view, () => {
              return Px.template`
                <div class="px-cut-prints-header">
                  <div class="px-actions px-actions-left">
                    <button data-px-tooltip="${this.editButtonTitle}"
                            data-onclick="editSelectedPageSet"
                            ${this.editButtonEnabled ? '' : 'disabled'}>
                      ${Px.t('Edit image')}
                      ${Px.raw(CutPrints.icons.edit_image)}
                    </button>
                    <button data-px-tooltip="${this.deleteButtonTitle}"
                            data-onclick="deleteSetsModal"
                            ${this.deleteButtonEnabled ? '' : 'disabled'}>
                      ${Px.t('Delete images')}
                      ${Px.raw(CutPrints.icons.trashcan)}
                    </button>
                  </div>
                  <div class="px-title">
                    <h2>${this.cutPrintQuantityInfo}</h2>
                    ${Px.if(this.cutPrintStore.selected_sets.length > 0, () => {
                      return Px.template`
                        <div class="px-separator">&bull;</div>
                        <div class="px-quantity-count-info">
                          ${Px.t('{{count}} selected').replace('{{count}}', this.cutPrintStore.selected_sets.length)}
                        </div>
                      `;
                    })}
                  </div>
                  <div class="px-actions px-actions-right">
                    <div class="px-quantity-controls">
                      <div class="px-quantity-title">
                        ${Px.t('Change quantity')}
                      </div>
                      <div class="px-controls">
                        <button data-onclick="decrementQuantity" data-px-tooltip="${this.decrementQuantityTitle}"
                                ${this.decrementQuantityButtonEnabled ? '' : 'disabled'}>
                          ${Px.raw(CutPrints.icons.decrement_quantity)}
                        </button>
                        <input type="number" min="1" max="${CutPrints.MAX_PRINT_QUANTITY}" step="1"
                              data-onchange="setQuantity" value="${this.quantityInputValue}"
                              ${this.quantityInputEnabled ? '' : 'disabled'}
                        />
                        <button data-onclick="incrementQuantity" data-px-tooltip="${this.incrementQuantityTitle}"
                                ${this.incrementQuantityButtonEnabled ? '' : 'disabled'}>
                          ${Px.raw(CutPrints.icons.increment_quantity)}
                        </button>
                      </div>
                    </div>
                    ${Px.if(this.areAllSetsSelected, () => {
                      return Px.template`
                        <button class="px-select-all"
                                data-onclick="deselectAll">
                          ${Px.t('Deselect All')}
                        </button>
                      `;
                    }).else(() => {
                      return Px.template`
                        <button class="px-select-all"
                                data-onclick="selectAll">
                          ${Px.t('Select All')}
                        </button>
                      `;
                    })}
                  </div>
                </div>
              `;
            })}
            <div class="px-page-sets" style="--thumb-size:${this.thumbnailSize}px;">
              ${this.printSetsWithDefinitions.map(set_with_definition => {
                const set = set_with_definition.set;
                const definition = set_with_definition.definition;
                return r(Px.Editor.CutPrints.PageSet, `set-${set.id}`, this.pageSetProps(set, definition));
              })}
            </div>
          `;
        }).elseIf(store.project.loaded, () => {
          return Px.template`
            <div class="px-cut-print-upload-instructions">
              <p>
                ${Px.if(store.ui.editor_mode === 'mobile', () => {
                  return Px.t('No images imported.');
                }).else(() => {
                  return Px.t('Drag & drop your photos here.');
                })}
              </p>
            </div>
          `;
        })}
      </div>
    `;
  }

  constructor(props) {
    super(props);
    this.selectOrDeselectSet = this.selectOrDeselectSet.bind(this);
    this.editPageSet = this.editPageSet.bind(this);

    let scroll_raf = null;
    let prev_set_count = null;
    // A reaction that scrolls to the last cut print every time a new print is added.
    this.registerReaction(() => this.printSetsWithDefinitions, (sets, prev_sets) => {
      if (scroll_raf) {
        cancelAnimationFrame(scroll_raf);
      }

      if (prev_set_count !== null && sets.length > prev_set_count) {
        scroll_raf = requestAnimationFrame(() => {
          if (this.dom_node) {
            const element = this.dom_node.querySelector('.px-page-sets .px-page-set:last-child');
            if (element) {
              element.scrollIntoView({behavior: 'smooth'});
            }
          }
        });
      }

      prev_set_count = sets.length;
    }, {
      name: 'Px.Editor.CutPrints::ScrollToNewSetReaction'
    });
  }

  get dataProperties() {
    return {
      store: {required: true}
    };
  }

  static get properties() {
    return {
      drop_active: {type: 'bool', std: false}
    };
  }

  static get computedProperties() {
    return {
      projectStore: function() {
        return this.data.store.project;
      },
      cutPrintStore: function() {
        return this.data.store.cut_prints;
      },
      printSetsWithDefinitions: function() {
        return this.projectStore.cut_print_sets_with_definitions;
      },
      editButtonEnabled: function() {
        return this.cutPrintStore.selected_sets.length === 1;
      },
      deleteButtonEnabled: function() {
        return this.cutPrintStore.selected_sets.length > 0;
      },
      deleteButtonTitle: function() {
        if (this.deleteButtonEnabled) {
          return Px.t('Click to delete selected images.');
        } else if (this.cutPrintStore.selected_sets.length === 0) {
          return Px.t('Select images to delete.');
        }
      },
      editButtonTitle: function() {
        if (this.editButtonEnabled) {
          return Px.t('Click to edit selected image.');
        } else if (this.cutPrintStore.selected_sets.length === 0) {
          return Px.t('Select image to edit.');
        } else {
          return Px.t('Select a single image to edit.');
        }
      },
      incrementQuantityButtonEnabled: function() {
        for (const set of this.cutPrintStore.selected_sets) {
          if (set.pages[0].quantity < CutPrints.MAX_PRINT_QUANTITY) {
            return true;
          }
        }
        return false;
      },
      decrementQuantityButtonEnabled: function() {
        for (const set of this.cutPrintStore.selected_sets) {
          if (set.pages[0].quantity > 1) {
            return true;
          }
        }
        return false;
      },
      incrementQuantityTitle: function() {
        if (this.cutPrintStore.selected_sets.length === 0) {
          return Px.t('Select images to change quantity.');
        }
        return Px.t('Increment quantity');
      },
      decrementQuantityTitle: function() {
        if (this.cutPrintStore.selected_sets.length === 0) {
          return Px.t('Select images to change quantity.');
        }
        return Px.t('Decrement quantity');
      },
      quantityInputEnabled: function() {
        return this.cutPrintStore.selected_sets.length > 0;
      },
      quantityInputValue: function() {
        const values = _.uniq(this.cutPrintStore.selected_sets.map(set => set.pages[0].quantity));
        if (values.length === 1) {
          return values[0];
        }
        return '';
      },
      areAllSetsSelected: function() {
        return this.printSetsWithDefinitions.length === this.cutPrintStore.selected_sets.length;
      },
      thumbnailSize: function() {
        const store = this.data.store;
        let thumb_size = 150;
        if (store.ui.editor_mode === 'mobile') {
          thumb_size = 96;
          // These values have to be kept in sync with the values from the CSS file...
          const side_padding = 8;
          const thumb_border_width = 2;
          const thumb_horizontal_padding = 8;
          const available_width = window.screen.availWidth - 2 * side_padding;
          // If we cannot fit three 96px thumbnails in a row, scale the thumbnails up to take the
          // the most available space possible.
          if (available_width / (thumb_size + 2 * thumb_border_width + 2 * thumb_horizontal_padding) < 3) {
            thumb_size = Math.floor((available_width - 4 * thumb_border_width - 4 * thumb_horizontal_padding) / 2);
          }
        }
        return thumb_size;
      },
      cutPrintQuantityInfo: function() {
        const store = this.data.store;
        const total_prints = store.total_cut_print_quantity;
        const max_prints = store.theme.max_cut_print_quantity;
        if (max_prints > 0) {
          let tpl = Px.t('{{count}}/{{max_count}} prints total');
          return tpl.replace('{{count}}', total_prints).replace('{{max_count}}', max_prints);
        } else {
          return Px.t('{{count}} prints total').replace('{{count}}', total_prints);
        }
      }
    };
  }

  selectOrDeselectSet(set) {
    const store = this.data.store;
    if (store.ui.share_view) {
      return;
    }
    if (store.ui.editor_mode === 'mobile' && store.mobile.view_mode !== 'cut-prints-quantity-selection') {
      store.mobile.animate(() => store.selectSet(set));
    } else {
      mobx.runInAction(() => {
        if (this.cutPrintStore.isSelected(set)) {
          this.cutPrintStore.deselect(set);
        } else {
          this.cutPrintStore.select(set);
        }
      });
    }
  }

  pageSetProps(set, definition) {
    return {
      set: set,
      set_definition: definition,
      selected: this.cutPrintStore.isSelected(set),
      thumbnail_size: this.thumbnailSize,
      selectOrDeselectSet: this.selectOrDeselectSet,
      onDblClick: this.editPageSet
    };
  }

  editPageSet(set) {
    mobx.runInAction(() => {
      const store = this.data.store;
      store.selectSet(set);
      this.cutPrintStore.deselectAll();
      this.cutPrintStore.select(set);
      const image = store.selected_page.image_elements.find(image => image.edit);
      if (image) {
        store.selectElement(image);
      }
    });
  }

  // --------------
  // Event handlers
  // --------------

  selectAll(evt) {
    mobx.runInAction(() => {
      this.printSetsWithDefinitions.forEach(set_with_def => {
        this.cutPrintStore.select(set_with_def.set);
      });
    });
  }

  deselectAll(evt) {
    this.cutPrintStore.deselectAll();
  }

  editSelectedPageSet(evt) {
    this.editPageSet(this.cutPrintStore.selected_sets[0]);
  }

  deleteSetsModal(evt) {
    this.makeModal(Px.Editor.CutPrints.DeletePageSetsModal, {
      deleteSets: this.deletePageSets.bind(this)
    });
  }

  setQuantity(evt) {
    const store = this.data.store;

    if (evt.currentTarget.reportValidity()) {
      const quantity = parseInt(evt.currentTarget.value, 10);

      if (quantity >= 1 && quantity <= CutPrints.MAX_PRINT_QUANTITY) {

        const max_prints = store.theme.max_cut_print_quantity;

        if (max_prints > 0) {
          let total_quantity = 0;
          store.project.cut_print_sets_with_definitions.forEach(set_with_definition => {
            const set = set_with_definition.set;
            if (this.cutPrintStore.isSelected(set)) {
              set.pages.forEach(page => {
                total_quantity += quantity;
              });
            } else {
              set.pages.forEach(page => {
                total_quantity += page.quantity;
              });
            }
          });

          if (total_quantity > max_prints) {
            let message = Px.t(CutPrints.MAX_PRINT_QUANTITY_ERROR_MESSAGE);
            message = message.replace('{{count}}', max_prints);
            store.showNotification(message, 'error');
            return;
          }
        }

        mobx.runInAction(() => {
          store.undo_redo.withUndo(() => {
            this.cutPrintStore.selected_sets.forEach(set => {
              set.pages.forEach(page => {
                page.quantity = quantity;
              });
            });
          }, {
            label: 'set quantity'
          });
        });
      }
    }
  }

  incrementQuantity(evt) {
    const store = this.data.store;
    if (this.incrementQuantityButtonEnabled) {

      const max_prints = store.theme.max_cut_print_quantity;

      if (max_prints > 0) {
        let total_quantity = 0;
        store.project.cut_print_sets_with_definitions.forEach(set_with_definition => {
          const set = set_with_definition.set;
          if (this.cutPrintStore.isSelected(set)) {
            set.pages.forEach(page => {
              total_quantity += Math.min(page.quantity + 1, CutPrints.MAX_PRINT_QUANTITY);
            });
          } else {
            set.pages.forEach(page => {
              total_quantity += page.quantity;
            });
          }
        });

        if (total_quantity > max_prints) {
          let message = Px.t(CutPrints.MAX_PRINT_QUANTITY_ERROR_MESSAGE);
          message = message.replace('{{count}}', max_prints);
          store.showNotification(message, 'error');
          return;
        }
      }

      this.data.store.undo_redo.withUndo(() => {
        this.cutPrintStore.selected_sets.forEach(set => {
          set.pages.forEach(page => {
            if (page.quantity < CutPrints.MAX_PRINT_QUANTITY) {
              page.quantity++;
            }
          });
        });
      }, {
        label: 'increment quantity'
      });
    }
  }

  decrementQuantity(evt) {
    if (this.decrementQuantityButtonEnabled) {
      mobx.runInAction(() => {
        this.data.store.undo_redo.withUndo(() => {
          this.cutPrintStore.selected_sets.forEach(set => {
            set.pages.forEach(page => {
              if (page.quantity > 1) {
                page.quantity--;
              }
            });
          });
        }, {
          label: 'decrement quantity'
        });
      });
    }
  }

  onItemsDropped(evt) {
    evt.preventDefault();
    let files = Px.Util.getDataTransferFiles(evt);
    files = Px.LocalFiles.Validation.filterAndValidateFiles(files);
    if (files) {
      this.data.store.importCutPrintImageFiles(files);
    }
    this.state.drop_active = false;
  }

  onDragEnter(evt) {
    evt.preventDefault();
  }

  onDragOver(evt) {
    evt.preventDefault();
    this.state.drop_active = true;
  }

  onDragLeave(evt) {
    this.state.drop_active = false;
  }

  // -------
  // Private
  // -------

  deletePageSets() {
    mobx.runInAction(() => {
      this.data.store.deleteSets(this.cutPrintStore.selected_sets);
      this.cutPrintStore.deselectAll();
    });
  }

};

Px.Editor.CutPrints.MAX_PRINT_QUANTITY = 999;
Px.Editor.CutPrints.MAX_PRINT_QUANTITY_ERROR_MESSAGE = 'Cannot set quantity. Maximum number of prints is {{count}}';
Px.Editor.CutPrints.icons = {
  decrement_quantity: '<svg width="10" height="2" viewBox="0 0 10 2"><path d="M9.33333 1.33333H0V0H9.33333V1.33333Z" fill="currentColor"/></svg>',
  increment_quantity: '<svg width="10" height="10" viewBox="0 0 10 10"><path d="M9.66683 5.33333H5.66683V9.33333H4.3335V5.33333H0.333496V4H4.3335V0H5.66683V4H9.66683V5.33333Z" fill="currentColor"/></svg>',
  edit_image: '<svg width="15" height="15" viewBox="0 0 23 23" fill="none"><path d="M4.79167 10.6375L6.70833 8.72083L11.9792 13.9917L15.3333 10.6375L18.2083 13.5125V4.79167H4.79167V10.6375ZM3.83333 2.875H19.1667C19.4208 2.875 19.6646 2.97597 19.8443 3.15569C20.024 3.33541 20.125 3.57917 20.125 3.83333V19.1667C20.125 19.4208 20.024 19.6646 19.8443 19.8443C19.6646 20.024 19.4208 20.125 19.1667 20.125H3.83333C3.57917 20.125 3.33541 20.024 3.15569 19.8443C2.97597 19.6646 2.875 19.4208 2.875 19.1667V3.83333C2.875 3.57917 2.97597 3.33541 3.15569 3.15569C3.33541 2.97597 3.57917 2.875 3.83333 2.875ZM14.8542 9.58333C14.4729 9.58333 14.1073 9.43188 13.8377 9.1623C13.5681 8.89272 13.4167 8.52708 13.4167 8.14583C13.4167 7.76458 13.5681 7.39895 13.8377 7.12937C14.1073 6.85978 14.4729 6.70833 14.8542 6.70833C15.2354 6.70833 15.601 6.85978 15.8706 7.12937C16.1402 7.39895 16.2917 7.76458 16.2917 8.14583C16.2917 8.52708 16.1402 8.89272 15.8706 9.1623C15.601 9.43188 15.2354 9.58333 14.8542 9.58333Z" fill="currentColor"/></svg>',
  trashcan: '<svg width="17" height="17" viewBox="0 0 17 17"><path d="M10.02 1.8335C10.2998 1.83357 10.5726 1.92169 10.7995 2.08537C11.0265 2.24905 11.1962 2.48 11.2847 2.7455L11.6467 3.8335H13.8333C14.0101 3.8335 14.1797 3.90373 14.3047 4.02876C14.4298 4.15378 14.5 4.32335 14.5 4.50016C14.5 4.67697 14.4298 4.84654 14.3047 4.97157C14.1797 5.09659 14.0101 5.16683 13.8333 5.16683L13.8313 5.21416L13.2533 13.3095C13.2173 13.8139 12.9915 14.2859 12.6214 14.6305C12.2513 14.9751 11.7644 15.1668 11.2587 15.1668H5.74133C5.23564 15.1668 4.74874 14.9751 4.37864 14.6305C4.00855 14.2859 3.78274 13.8139 3.74667 13.3095L3.16867 5.2135C3.16746 5.19797 3.16679 5.1824 3.16667 5.16683C2.98986 5.16683 2.82029 5.09659 2.69526 4.97157C2.57024 4.84654 2.5 4.67697 2.5 4.50016C2.5 4.32335 2.57024 4.15378 2.69526 4.02876C2.82029 3.90373 2.98986 3.8335 3.16667 3.8335H5.35333L5.71533 2.7455C5.8038 2.47989 5.97362 2.24886 6.20073 2.08517C6.42784 1.92148 6.70071 1.83343 6.98067 1.8335H10.02ZM12.498 5.16683H4.502L5.07667 13.2142C5.08863 13.3823 5.16385 13.5397 5.28717 13.6546C5.4105 13.7695 5.57277 13.8334 5.74133 13.8335H11.2587C11.4272 13.8334 11.5895 13.7695 11.7128 13.6546C11.8362 13.5397 11.9114 13.3823 11.9233 13.2142L12.498 5.16683ZM7.16667 7.16683C7.32996 7.16685 7.48756 7.2268 7.60958 7.33531C7.73161 7.44381 7.80956 7.59333 7.82867 7.7555L7.83333 7.8335V11.1668C7.83315 11.3367 7.76808 11.5002 7.65143 11.6237C7.53479 11.7473 7.37536 11.8217 7.20574 11.8316C7.03611 11.8416 6.86908 11.7864 6.73878 11.6773C6.60848 11.5683 6.52474 11.4136 6.50467 11.2448L6.5 11.1668V7.8335C6.5 7.65668 6.57024 7.48712 6.69526 7.36209C6.82029 7.23707 6.98986 7.16683 7.16667 7.16683ZM9.83333 7.16683C10.0101 7.16683 10.1797 7.23707 10.3047 7.36209C10.4298 7.48712 10.5 7.65668 10.5 7.8335V11.1668C10.5 11.3436 10.4298 11.5132 10.3047 11.6382C10.1797 11.7633 10.0101 11.8335 9.83333 11.8335C9.65652 11.8335 9.48695 11.7633 9.36193 11.6382C9.23691 11.5132 9.16667 11.3436 9.16667 11.1668V7.8335C9.16667 7.65668 9.23691 7.48712 9.36193 7.36209C9.48695 7.23707 9.65652 7.16683 9.83333 7.16683ZM10.02 3.16683H6.98L6.758 3.8335H10.242L10.02 3.16683Z" fill="currentColor"/></g></svg>'
};

Px.Editor.CutPrints.PageSet = class PageSet extends Px.Editor.BaseComponent {

  template() {
    const store = this.data.store;

    return Px.template`
      <div class="px-page-set"
           data-set-id="${this.data.set.id}"
           data-selectable="${this.isSelectable}"
           data-selected="${this.data.selected}"
           data-onclick="selectOrDeselectPageSet"
           data-ondblclick="onDblClick">
        ${this.renderChild(Px.Editor.CutPrints.PageThumbs, 'page-thumbs', this.pageThumbProps)}
        <div class="px-page-captions">
          ${Px.if(this.data.set.left_caption, () => {
            return Px.template`
              <div class="px-page-caption" title="${this.data.set.left_caption}">
                ${this.data.set.left_caption}
              </div>
            `;
          })}
          ${Px.if(this.centerCaption, () => {
            return Px.template`
              <div class="px-page-caption" title="${this.centerCaption}">
                ${this.centerCaption}
              </div>
            `;
          })}
          ${Px.if(this.data.set.right_caption, () => {
            return Px.template`
              <div class="px-page-caption" title="${this.data.set.right_caption}">
                ${this.data.set.right_caption}
              </div>
            `;
          })}
        </div>

        ${Px.if(!(store.ui.editor_mode === 'mobile' || store.ui.share_view), () => {
          return Px.template`
            <div class="px-cut-print-controls">
              <button class="px-quantity-btn"
                      ${this.decrementQuantityButtonEnabled ? '' : 'disabled'}
                      data-onclick="decrementQuantity"
                      data-ondblclick="preventDblClick">
                &minus;
              </button>
              <span>
                  ${this.data.set.pages[0].quantity}
              </span>
              <button class="px-quantity-btn"
                      ${this.incrementQuantityButtonEnabled ? '' : 'disabled'}
                      data-onclick="incrementQuantity"
                      data-ondblclick="preventDblClick">
                &plus;
              </button>
              <button class="px-delete-btn"
                      data-onclick="deletePageSetModal"
                      data-ondblclick="preventDblClick">
                ${Px.raw(Px.Editor.CutPrints.icons.trashcan)}
              </button>
            </div>
          `;
        })}
      </div>
    `;
  }

  get dataProperties() {
    return {
      store: {required: true},
      set: {required: true},
      set_definition: {required: true},
      selected: {std: false},
      thumbnail_size: {required: true},
      selectOrDeselectSet: {required: true},
      onDblClick: {required: false}
    };
  }

  static get computedProperties() {
    return {
      isSelectable: function() {
        const set = this.data.set;
        const definition = this.data.set_definition;
        // Only FULL sets from the grow set can be deleted/duplicated.
        // Partially filled sets can never be deleted/duplicated or moved around.
        return definition.grow && set.pages.length === definition.pages.length;
      },
      pageThumbProps: function() {
        return {
          set: this.data.set,
          is_selectable: this.isSelectable,
          thumbnail_size: this.data.thumbnail_size
        }
      },
      centerCaption: function() {
        const store = this.data.store;
        const set = this.data.set;
        if (!(set.left_caption || set.center_caption || set.right_caption)) {
          const image_element = set.pages[0].image_elements.find(e => e.is_editable_master_element && e.id);
          if (image_element) {
            const image = store.images.get(image_element.id);
            if (image && image.filename) {
              return Px.Util.truncateFilename(image.filename, store.ui.editor_mode === 'mobile' ? 18 : 26);
            }
          }
        }
        return set.center_caption;
      },
      decrementQuantityButtonEnabled: function() {
        return this.data.set.pages[0].quantity > 1;
      },
      incrementQuantityButtonEnabled: function() {
        return this.data.set.pages[0].quantity < Px.Editor.CutPrints.MAX_PRINT_QUANTITY;
      }
    };
  }

  // --------------
  // Event handlers
  // --------------

  onDblClick(evt) {
    if (this.data.onDblClick) {
      this.data.onDblClick(this.data.set);
    }
  }

  incrementQuantity(evt) {
    evt.stopPropagation();

    const store = this.data.store;
    const max_prints = store.theme.max_cut_print_quantity;

    if (max_prints > 0 && store.total_cut_print_quantity >= max_prints) {
      let message = Px.t(Px.Editor.CutPrints.MAX_PRINT_QUANTITY_ERROR_MESSAGE);
      message = message.replace('{{count}}', max_prints);
      store.showNotification(message, 'error');
      return;
    }

    store.undo_redo.withUndo(() => {
      this.data.set.pages.forEach(page => {
        if (page.quantity < Px.Editor.CutPrints.MAX_PRINT_QUANTITY) {
          page.quantity++;
        }
      });
    }, {
      label: 'increment quantity'
    });
  }

  decrementQuantity(evt) {
    evt.stopPropagation();

    this.data.store.undo_redo.withUndo(() => {
      this.data.set.pages.forEach(page => {
        if (page.quantity > 1) {
          page.quantity--;
        }
      });
    }, {
      label: 'decrement quantity'
    });
  }

  selectOrDeselectPageSet(evt) {
    if (this.isSelectable) {
      this.data.selectOrDeselectSet(this.data.set);
    }
  }

  deletePageSetModal(evt) {
    evt.stopPropagation();
    this.makeModal(Px.Editor.CutPrints.DeletePageSetsModal, {
      deleteSets: () => mobx.runInAction(() => {
        const store = this.data.store;
        const set = this.data.set;
        store.deleteSets([set]);
        store.cut_prints.deselect(set);
      })
    });
  }

  preventDblClick(evt) {
    evt.preventDefault();
    evt.stopPropagation();
  }

};

Px.Editor.CutPrints.PageThumbs = class PageThumbs extends Px.Editor.BaseComponent {

  template() {
    const r = this.renderChild;
    return Px.template`
      <div class="px-page-thumbs" ${this.cutPrintDimensions}>
        ${this.data.set.pages.map(page => {
          return this.renderChild(Px.Editor.Page, `page-${page.id}`, this.pageProps(page));
        })}
        ${r(Px.Editor.ImageUploadProgressIndicator, 'upload-progress-indicator', {image: this.image})}
      </div>
    `;
  }

  get dataProperties() {
    return {
      set: {required: true},
      is_selectable: {required: true},
      thumbnail_size: {required: true},
      store: {required: true}
    };
  }

  static get computedProperties() {
    return {
      cutPrintDimensions: function() {
        const store = this.data.store;
        const page = this.data.set.pages[0];
        const scale = this.data.thumbnail_size / Math.max(page.width, page.height);
        let width = page.width * scale;
        let height = page.height * scale;
        if (store.isAutorotatedCutPrint(page)) {
          [width, height] = [height, width];
        }
        return Px.raw(`style="--cut-print-width:${width}px;--cut-print-height:${height}px;"`);
      },
      image: function() {
        const page = this.data.set.pages[0];
        if (page) {
          const image_element = page.image_elements.find(image_element => {
            const image = image_element.image;
            return image.type === 'local';
          });
          if (image_element) {
            return image_element.image;
          }
        }
        return null;
      }
    };
  }

  pageProps(page) {
    const height = this.data.thumbnail_size;
    const width = height;
    return {
      page: page,
      available_width: width,
      available_height: height,
      preview_mode: true,
      render_controls: false
    };
  }

};

Px.Editor.CutPrints.DeletePageSetsModal = class DeletePageSetsModal extends Px.Components.BaseModal {

  get title() {
    return Px.t('Are you sure you want to delete selected photos?');
  }

  get footer() {
    return Px.template`
      <button class="px-cancel" data-onclick="cancel">
        ${Px.t('Cancel')}
      </button>
      <button class="px-delete" data-onclick="deleteSets">
        ${Px.t('Delete')}
      </button>
    `;
  }

  get css_class() {
    return `${super.css_class} px-confirmation-modal`;
  }

  constructor(props) {
    super(props);
    this.close_on_background_click = false;
  }

  get dataProperties() {
    return {
      deleteSets: {required: true}
    }
  }

  // --------------
  // Event handlers
  // --------------

  cancel(evt) {
    this.destroy();
  }

  deleteSets(evt) {
    this.data.deleteSets();
    this.destroy();
  }

};
